import React, { useState } from 'react';
import { Copy, Check, Link as LinkIcon, AlertCircle } from 'lucide-react';

import { createRoot } from 'react-dom/client';

const ToastComponent = ({
	message,
	type = 'success',
	onDone,
}: {
	message: string;
	type?: 'success' | 'error';
	onDone: () => void;
}) => {
	React.useEffect(() => {
		const t = setTimeout(onDone, 3000);
		return () => clearTimeout(t);
	}, [onDone]);

	return (
		<div
			style={{
				position: 'fixed',
				bottom: 28,
				left: '50%',
				transform: 'translateX(-50%)',
				zIndex: 99999,
				background: '#1e293b',
				color: 'white',
				padding: '10px 20px',
				borderRadius: 12,
				fontSize: 13,
				fontWeight: 500,
				boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
				whiteSpace: 'nowrap',
				display: 'flex',
				alignItems: 'center',
				gap: 8,
				animation: 'mapToastIn 0.25s ease',
			}}
		>
			<style>{`@keyframes mapToastIn { from { opacity:0; transform:translateX(-50%) translateY(8px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }`}</style>
			{type === 'success' ? (
				<Check size={16} style={{ color: '#4ade80', flexShrink: 0 }} />
			) : (
				<AlertCircle size={16} style={{ color: '#f87171', flexShrink: 0 }} />
			)}
			{message}
		</div>
	);
};

export const showGlobalToast = (message: string, type: 'success' | 'error' = 'success') => {
	const existingContainer = document.getElementById('global-toast-root');
	if (existingContainer) {
		existingContainer.remove();
	}

	const container = document.createElement('div');
	container.id = 'global-toast-root';
	document.body.appendChild(container);

	const root = createRoot(container);
	root.render(
		<ToastComponent
			message={message}
			type={type}
			onDone={() => {
				setTimeout(() => {
					root.unmount();
					if (container.parentNode) {
						container.parentNode.removeChild(container);
					}
				}, 0);
			}}
		/>
	);
};

interface ShareButtonProps {
	url: string;
	title: string;
}

const SharingTab: React.FC<ShareButtonProps> = ({ url, title }) => {
	const [copied, setCopied] = useState(false);
	const [showLeftShadow, setShowLeftShadow] = useState(false);
	const [showRightShadow, setShowRightShadow] = useState(false);
	const scrollRef = React.useRef<HTMLDivElement>(null);

	const checkScroll = () => {
		if (scrollRef.current) {
			const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
			setShowLeftShadow(scrollLeft > 5);
			setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 5);
		}
	};

	React.useEffect(() => {
		checkScroll();
		window.addEventListener('resize', checkScroll);
		return () => window.removeEventListener('resize', checkScroll);
	}, [url]);

	const handleCopy = async (e: React.MouseEvent) => {
		e.stopPropagation();
		try {
			await navigator.clipboard.writeText(url);
			setCopied(true);

			// Show the global toast which survives unmounts
			showGlobalToast('Link copied to clipboard!', 'success');

			setTimeout(() => setCopied(false), 3000);
		} catch (err) {
			console.error('Failed to copy: ', err);
			showGlobalToast('Failed to copy link', 'error');
		}
	};

	return (
		<div className='p-3 w-[280px] bg-white rounded-xl shadow-sm'>
			<div className='flex flex-col space-y-3'>
				<div className='flex items-center space-x-2 text-gray-500 mb-1'>
					<LinkIcon size={16} />
					<span className='text-xs font-semibold uppercase tracking-wider'>Share Post</span>
				</div>

				<div className='relative p-2 bg-gray-50 rounded-lg border border-gray-100 group transition-all hover:bg-gray-100 overflow-hidden'>
					{/* Dynamic Scroll Shadows */}
					{showLeftShadow && (
						<div className='absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-gray-50/80 to-transparent pointer-events-none z-10 transition-opacity duration-300'></div>
					)}
					{showRightShadow && (
						<div className='absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-gray-50/80 to-transparent pointer-events-none z-10 transition-opacity duration-300'></div>
					)}

					<div
						ref={scrollRef}
						onScroll={checkScroll}
						className='text-sm text-gray-600 font-medium px-2 overflow-x-auto whitespace-nowrap custom-scrollbar no-scrollbar scroll-smooth'
					>
						{url}
					</div>
				</div>

				<button
					onClick={handleCopy}
					className='w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-lg shadow-md hover:shadow-lg active:scale-[0.98] transition-all flex items-center justify-center gap-2'
				>
					{copied ? (
						<>
							<Check size={18} />
							<span>Copied!</span>
						</>
					) : (
						<>
							<Copy size={18} />
							<span>Copy Link</span>
						</>
					)}
				</button>
			</div>
		</div>
	);
};

export default SharingTab;
